/**
 * @fileoverview Modal component for creating, editing and configuring RunToGoal (Tiro a Gol V2) themes.
 * Supports 5 navigation tabs:
 * 1. General & Cover (Name, Game Mode ['soccer', 'cars', 'runners', 'custom'], Title, Subtitle, Logos, Background, Colors).
 * 2. Track & Mode (Track BG Type, Color, Image, Text Color, HasBall checkbox, FinishGapBase, FinishGapStep).
 * 3. Dynamic N Zones / Lanes (Add, remove, and configure N zones with zoneId, name, color, avatarUrl, sparkUrl, winnerAvatarUrl).
 * 4. Outro / Goal Animation (Outro BG, animated element, width px, translateY px, scale, winner text template).
 * 5. Mobile Fan View (Fan BG Type, Color, Image, Text Color, Tap button icon).
 * 
 * Implements Drag & Drop media uploads calling UploadMedia from BL/RunToGoal,
 * instant preview, defensive validation, and feedback with useFloatMessage.
 * 
 * @module components/Admin/Games/RunToGoal/RunToGoalThemeModal
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { UploadMedia, SaveTheme, ResolveMediaUrl } from '../../../../utilities/BL/RunToGoal';
import { GetActiveStadiumZones } from '../../../../utilities/BL/Zones';

/**
 * Resuelve y sanitiza la URL absoluta para la previsualización de archivos multimedia.
 * 
 * @param {string|null} mediaPath - Ruta relativa o URL absoluta.
 * @returns {string} URL formateada para previsualización.
 */
export const getFullAssetUrl = (mediaPath) => {
    return ResolveMediaUrl(mediaPath);
};

/**
 * Componente Drag & Drop Reutilizable para campos individuales de multimedia.
 * 
 * @function DropzoneField
 * @param {Object} props - Propiedades del componente.
 * @param {string} [props.label] - Etiqueta descriptiva del campo.
 * @param {string} props.fieldName - Clave del campo en el estado del formulario.
 * @param {string} props.category - Categoría del asset (cover, track, zones, outro, fan).
 * @param {string} [props.value=''] - URL actual del recurso multimedia.
 * @param {Function} props.onUpload - Callback para procesar la subida del archivo.
 * @param {Function} props.onRemove - Callback para limpiar el recurso seleccionado.
 * @param {boolean} [props.isUploading=false] - Indicador de subida activa.
 * @param {string} [props.helpText=''] - Texto de ayuda contextual.
 * @param {string} [props.aspectHint=''] - Recomendación de formato o resolución.
 * @param {string} [props.accept='image/*'] - Tipos MIME aceptados.
 * @param {string} [props.previewHeight='85px'] - Altura máxima de previsualización.
 * @param {number} [props.zoneIndex=-1] - Índice de la zona (si aplica).
 * @param {string} [props.zoneSubField=null] - Subcampo de la zona (si aplica).
 * @returns {React.ReactElement} Campo de carga con drag & drop y preview.
 */
const DropzoneField = ({
    label,
    fieldName,
    category,
    value = '',
    onUpload,
    onRemove,
    isUploading = false,
    helpText = '',
    aspectHint = '',
    accept = 'image/*',
    previewHeight = '85px',
    zoneIndex = -1,
    zoneSubField = null
}) => {
    const [isDragHoverActive, setIsDragHoverActive] = useState(false);
    const inputElementId = `runtogoal-file-input-${fieldName}${zoneIndex >= 0 ? `-${zoneIndex}` : ''}${zoneSubField ? `-${zoneSubField}` : ''}`;

    const handleDragOver = (dragEvent) => {
        dragEvent.preventDefault();
        dragEvent.stopPropagation();
        setIsDragHoverActive(true);
    };

    const handleDragLeave = (dragEvent) => {
        dragEvent.preventDefault();
        dragEvent.stopPropagation();
        setIsDragHoverActive(false);
    };

    const handleDrop = (dropEvent) => {
        dropEvent.preventDefault();
        dropEvent.stopPropagation();
        setIsDragHoverActive(false);
        const droppedFiles = dropEvent.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
            onUpload(droppedFiles[0], category, fieldName, zoneIndex, zoneSubField);
        }
    };

    const handleNativeFileChange = (changeEvent) => {
        const selectedFiles = changeEvent.target.files;
        if (selectedFiles && selectedFiles.length > 0) {
            onUpload(selectedFiles[0], category, fieldName, zoneIndex, zoneSubField);
            changeEvent.target.value = '';
        }
    };

    return (
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
                {label && (
                    <label className="form-label fw-bold text-dark small mb-0">
                        {label}
                    </label>
                )}
                {aspectHint && (
                    <span className="badge bg-light text-muted border small">
                        {aspectHint}
                    </span>
                )}
            </div>

            <div
                className={`border border-2 rounded-3 p-3 text-center position-relative transition-all d-flex flex-column align-items-center justify-content-center ${
                    isDragHoverActive
                        ? 'border-primary bg-primary-subtle shadow-sm'
                        : 'border-secondary-subtle bg-light'
                }`}
                style={{
                    borderStyle: 'dashed',
                    minHeight: '105px',
                    cursor: isUploading ? 'not-allowed' : 'pointer'
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => {
                    if (!isUploading) {
                        const inputEl = document.getElementById(inputElementId);
                        if (inputEl) inputEl.click();
                    }
                }}
            >
                <input
                    id={inputElementId}
                    type="file"
                    className="d-none"
                    accept={accept}
                    onChange={handleNativeFileChange}
                    disabled={isUploading}
                />

                {isUploading ? (
                    <div className="py-2 d-flex flex-column align-items-center">
                        <div className="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
                        <span className="small text-primary fw-bold">Subiendo y optimizando archivo...</span>
                    </div>
                ) : value ? (
                    <div
                        className="position-relative d-flex flex-column align-items-center w-100"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={getFullAssetUrl(value)}
                            alt="Vista previa"
                            className="img-thumbnail shadow-sm bg-white mb-2"
                            style={{
                                maxHeight: previewHeight,
                                maxWidth: '100%',
                                objectFit: 'contain'
                            }}
                            onError={(errorEvent) => {
                                errorEvent.target.style.display = 'none';
                            }}
                        />
                        <div className="d-flex gap-2 align-items-center">
                            <span
                                className="small text-truncate text-muted"
                                style={{ maxWidth: '180px' }}
                                title={value}
                            >
                                {value.split('/').pop()}
                            </span>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger py-0 px-2"
                                title="Eliminar archivo"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemove(fieldName, zoneIndex, zoneSubField);
                                }}
                            >
                                <i className="bi bi-trash3"></i>
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary py-0 px-2"
                                title="Reemplazar archivo"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const inputEl = document.getElementById(inputElementId);
                                    if (inputEl) inputEl.click();
                                }}
                            >
                                <i className="bi bi-arrow-repeat"></i>
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <i className="bi bi-cloud-arrow-up text-primary fs-3 d-block mb-1"></i>
                        <span className="small text-muted d-block">
                            Arrastra aquí tu archivo o haz <strong className="text-primary">clic para buscar</strong>
                        </span>
                        {helpText && (
                            <small className="text-secondary d-block mt-1 font-monospace" style={{ fontSize: '0.75rem' }}>
                                {helpText}
                            </small>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

DropzoneField.propTypes = {
    label: PropTypes.string,
    fieldName: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    value: PropTypes.string,
    onUpload: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    isUploading: PropTypes.bool,
    helpText: PropTypes.string,
    aspectHint: PropTypes.string,
    accept: PropTypes.string,
    previewHeight: PropTypes.string,
    zoneIndex: PropTypes.number,
    zoneSubField: PropTypes.string
};

/**
 * Plantilla de estructura inicial para temas visuales de Tiro a Gol V2.
 */
const DEFAULT_THEME_STATE = {
    id: 0,
    name: '',
    gameModeType: 'soccer', // 'soccer' | 'cars' | 'runners' | 'custom'
    isSystemPreset: false,

    // Pestaña 1: Portada
    coverTitle: '¡TIRO A GOL!',
    coverSubtitle: '¡Presiona rápidamente tu celular para llevar a tu equipo a la victoria!',
    coverBackgroundType: 'color', // 'color' | 'image'
    coverBackgroundColor: '#0f172a',
    coverBackgroundUrl: '',
    coverTextColor: '#ffffff',
    brandLogoUrl: '',
    sponsorLogoUrl: '',

    // Pestaña 2: Pista y Modo
    trackBackgroundType: 'color', // 'color' | 'image'
    trackBackgroundColor: '#064e3b',
    trackBackgroundUrl: '',
    trackTextColor: '#ffffff',
    finishGapBase: 20,
    finishGapStep: 4,
    hasBall: true,

    // Pestaña 3: Configuración Dinámica de N Zonas
    zones: [
        {
            zoneId: 'zona1',
            name: 'Xolo Mayor Rojo',
            color: '#ef4444',
            avatarUrl: '/img/games/runtogoal/xolo-caricatura-1.png',
            sparkUrl: '/img/games/runtogoal/destellos-rojos.png',
            winnerAvatarUrl: '/img/games/runtogoal/xolo-caricatura-1.png'
        },
        {
            zoneId: 'zona2',
            name: 'Xolo Mayor Blanco',
            color: '#3b82f6',
            avatarUrl: '/img/games/runtogoal/xolo-caricatura-2.png',
            sparkUrl: '/img/games/runtogoal/destellos-blancos.png',
            winnerAvatarUrl: '/img/games/runtogoal/xolo-caricatura-2.png'
        }
    ],

    // Pestaña 4: Animación Outro / Meta
    outroBackgroundUrl: '',
    outroElementUrl: '/img/games/runtogoal/balon-grande.png',
    outroElementWidth: 300,
    outroTranslateY: 0,
    outroScale: 1.0,
    winnerTextTemplate: '¡GOOOOOL DE {GANADOR}!',

    // Pestaña 5: Vista Móvil Aficionado
    fanBackgroundType: 'color', // 'color' | 'image'
    fanBackgroundColor: '#0f172a',
    fanBackgroundUrl: '',
    fanTextColor: '#ffffff',
    fanButtonIcon: ''
};

/**
 * Modos de juego soportados con metadata descriptiva.
 */
const GAME_MODES = [
    {
        id: 'soccer',
        name: 'Fútbol (Tiro a Gol)',
        icon: 'bi-dribbble',
        description: 'Con balón y animación de tiro al arco / portería',
        defaultHasBall: true,
        defaultCoverTitle: '¡TIRO A GOL!',
        defaultWinnerText: '¡GOOOOOL DE {GANADOR}!',
        defaultTrackBgColor: '#064e3b',
        defaultOutroElement: '/img/games/runtogoal/balon-grande.png'
    },
    {
        id: 'cars',
        name: 'Carrera de Autos / Pista',
        icon: 'bi-speedometer2',
        description: 'Vehículos compitiendo por llegar primero a la meta',
        defaultHasBall: false,
        defaultCoverTitle: '¡GRAN PREMIO!',
        defaultWinnerText: '¡VICTORIA PARA {GANADOR}!',
        defaultTrackBgColor: '#1e293b',
        defaultOutroElement: ''
    },
    {
        id: 'runners',
        name: 'Atletismo / Corredores',
        icon: 'bi-person-walking',
        description: 'Mascotas o atletas corriendo hacia la cinta de meta',
        defaultHasBall: false,
        defaultCoverTitle: '¡CARRERA DE ATLETISMO!',
        defaultWinnerText: '¡CAMPEÓN {GANADOR}!',
        defaultTrackBgColor: '#7f1d1d',
        defaultOutroElement: ''
    },
    {
        id: 'custom',
        name: 'Personalizado',
        icon: 'bi-gear-wide-connected',
        description: 'Configuración libre de fondos, avatares y elementos',
        defaultHasBall: false,
        defaultCoverTitle: '¡CARRERA PERSONALIZADA!',
        defaultWinnerText: '¡GANADOR {ZONE_NAME}!',
        defaultTrackBgColor: '#0f172a',
        defaultOutroElement: ''
    }
];

const ZONE_PALETTE_COLORS = [
    '#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6',
    '#ec4899', '#06b6d4', '#84cc16', '#6366f1', '#14b8a6'
];

/**
 * Modal para la creación, edición y personalización de temas de Tiro a Gol V2 (RunToGoal).
 * 
 * @function RunToGoalThemeModal
 * @param {Object} props - Propiedades del modal.
 * @param {boolean} [props.isOpen=false] - Control de visibilidad del modal.
 * @param {boolean} [props.show=false] - Alias de compatibilidad para isOpen.
 * @param {Function} [props.onCloseModal] - Callback para cerrar el modal.
 * @param {Function} [props.onClose] - Alias de compatibilidad para onCloseModal.
 * @param {Object|null} [props.initialThemeData=null] - Datos del tema a editar.
 * @param {Object|null} [props.themeToEdit=null] - Alias de compatibilidad para initialThemeData.
 * @param {Function} [props.onThemeSavedSuccessfully] - Callback invocado tras guardar exitosamente.
 * @param {Function} [props.onThemeSaved] - Alias de compatibilidad para onThemeSavedSuccessfully.
 * @param {Function} [props.onSave] - Alias de compatibilidad para onThemeSavedSuccessfully.
 * @param {string|null} [props.roomId=null] - Identificador GUID de la sala activa.
 * @param {string|null} [props.operatorId=null] - Identificador GUID del operador.
 * @returns {React.ReactElement|null} Modal interactivo de administración de temas.
 */
export const RunToGoalThemeModal = ({
    isOpen: propIsOpen,
    show,
    onCloseModal: propOnCloseModal,
    onClose,
    initialThemeData: propInitialThemeData,
    themeToEdit,
    onThemeSavedSuccessfully: propOnThemeSavedSuccessfully,
    onThemeSaved,
    onSave,
    roomId = null,
    operatorId = null
}) => {
    const isOpen = propIsOpen !== undefined ? propIsOpen : Boolean(show);
    const handleClose = propOnCloseModal || onClose || (() => {});
    const initialData = propInitialThemeData || themeToEdit || null;
    const handleThemeSaved = propOnThemeSavedSuccessfully || onThemeSaved || onSave || (() => {});

    const { showMessage } = useFloatMessage();
    const [activeTabKey, setActiveTabKey] = useState('general');
    const [isSavingInProgress, setIsSavingInProgress] = useState(false);
    const [activeUploadFieldKey, setActiveUploadFieldKey] = useState('');
    const [themeFormState, setThemeFormState] = useState(DEFAULT_THEME_STATE);

    /**
     * Parsea las zonas recibidas desde el DTO del backend o un JSON plano.
     */
    const parseZonesFromData = useCallback((data) => {
        if (!data) return DEFAULT_THEME_STATE.zones;

        if (Array.isArray(data.zones) && data.zones.length > 0) {
            return data.zones.map((z, idx) => ({
                zoneId: z.zoneId || z.ZoneId || `zona${idx + 1}`,
                name: z.name || z.Name || `Zona ${idx + 1}`,
                color: z.color || z.Color || ZONE_PALETTE_COLORS[idx % ZONE_PALETTE_COLORS.length],
                avatarUrl: z.avatarUrl || z.AvatarUrl || '',
                sparkUrl: z.sparkUrl || z.SparkUrl || '',
                winnerAvatarUrl: z.winnerAvatarUrl || z.WinnerAvatarUrl || z.avatarUrl || z.AvatarUrl || ''
            }));
        }

        if (Array.isArray(data.Zones) && data.Zones.length > 0) {
            return data.Zones.map((z, idx) => ({
                zoneId: z.zoneId || z.ZoneId || `zona${idx + 1}`,
                name: z.name || z.Name || `Zona ${idx + 1}`,
                color: z.color || z.Color || ZONE_PALETTE_COLORS[idx % ZONE_PALETTE_COLORS.length],
                avatarUrl: z.avatarUrl || z.AvatarUrl || '',
                sparkUrl: z.sparkUrl || z.SparkUrl || '',
                winnerAvatarUrl: z.winnerAvatarUrl || z.WinnerAvatarUrl || z.avatarUrl || z.AvatarUrl || ''
            }));
        }

        const rawJson = data.zonesConfigJson || data.ZonesConfigJson;
        if (typeof rawJson === 'string' && rawJson.trim().length > 0) {
            try {
                const parsed = JSON.parse(rawJson);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return parsed.map((z, idx) => ({
                        zoneId: z.zoneId || z.ZoneId || `zona${idx + 1}`,
                        name: z.name || z.Name || `Zona ${idx + 1}`,
                        color: z.color || z.Color || ZONE_PALETTE_COLORS[idx % ZONE_PALETTE_COLORS.length],
                        avatarUrl: z.avatarUrl || z.AvatarUrl || '',
                        sparkUrl: z.sparkUrl || z.SparkUrl || '',
                        winnerAvatarUrl: z.winnerAvatarUrl || z.WinnerAvatarUrl || z.avatarUrl || z.AvatarUrl || ''
                    }));
                }
            } catch (e) {
                console.warn('[RunToGoalThemeModal] Error al parsear zonesConfigJson:', e);
            }
        }

        return DEFAULT_THEME_STATE.zones;
    }, []);

    useEffect(() => {
        if (initialData && (initialData.id > 0 || initialData.Id > 0)) {
            const zones = parseZonesFromData(initialData);
            setThemeFormState({
                ...DEFAULT_THEME_STATE,
                ...initialData,
                id: initialData.id || initialData.Id || 0,
                name: initialData.name || initialData.Name || '',
                gameModeType: initialData.gameModeType || initialData.GameModeType || 'soccer',
                isSystemPreset: Boolean(initialData.isSystemPreset || initialData.IsSystemPreset),
                
                // Portada
                coverTitle: initialData.coverTitle ?? initialData.CoverTitle ?? DEFAULT_THEME_STATE.coverTitle,
                coverSubtitle: initialData.coverSubtitle ?? initialData.CoverSubtitle ?? DEFAULT_THEME_STATE.coverSubtitle,
                coverBackgroundType: initialData.coverBackgroundType || initialData.CoverBackgroundType || 'color',
                coverBackgroundColor: initialData.coverBackgroundColor || initialData.CoverBackgroundColor || '#0f172a',
                coverBackgroundUrl: initialData.coverBackgroundUrl || initialData.CoverBackgroundUrl || '',
                coverTextColor: initialData.coverTextColor || initialData.CoverTextColor || '#ffffff',
                brandLogoUrl: initialData.brandLogoUrl || initialData.BrandLogoUrl || '',
                sponsorLogoUrl: initialData.sponsorLogoUrl || initialData.SponsorLogoUrl || '',

                // Pista
                trackBackgroundType: initialData.trackBackgroundType || initialData.TrackBackgroundType || 'color',
                trackBackgroundColor: initialData.trackBackgroundColor || initialData.TrackBackgroundColor || '#064e3b',
                trackBackgroundUrl: initialData.trackBackgroundUrl || initialData.TrackBackgroundUrl || '',
                trackTextColor: initialData.trackTextColor || initialData.TrackTextColor || '#ffffff',
                finishGapBase: Number(initialData.finishGapBase ?? initialData.FinishGapBase ?? 20),
                finishGapStep: Number(initialData.finishGapStep ?? initialData.FinishGapStep ?? 4),
                hasBall: initialData.hasBall !== undefined ? Boolean(initialData.hasBall) : (initialData.HasBall !== undefined ? Boolean(initialData.HasBall) : true),

                // Zonas
                zones: zones,

                // Outro
                outroBackgroundUrl: initialData.outroBackgroundUrl || initialData.OutroBackgroundUrl || '',
                outroElementUrl: initialData.outroElementUrl || initialData.OutroElementUrl || '',
                outroElementWidth: Number(initialData.outroElementWidth ?? initialData.OutroElementWidth ?? 300),
                outroTranslateY: Number(initialData.outroTranslateY ?? initialData.OutroTranslateY ?? 0),
                outroScale: Number(initialData.outroScale ?? initialData.OutroScale ?? 1.0),
                winnerTextTemplate: initialData.winnerTextTemplate || initialData.WinnerTextTemplate || '¡GOOOOOL DE {GANADOR}!',

                // Fan
                fanBackgroundType: initialData.fanBackgroundType || initialData.FanBackgroundType || 'color',
                fanBackgroundColor: initialData.fanBackgroundColor || initialData.FanBackgroundColor || '#0f172a',
                fanBackgroundUrl: initialData.fanBackgroundUrl || initialData.FanBackgroundUrl || '',
                fanTextColor: initialData.fanTextColor || initialData.FanTextColor || '#ffffff',
                fanButtonIcon: initialData.fanButtonIcon || initialData.FanButtonIcon || ''
            });
        } else {
            setThemeFormState(DEFAULT_THEME_STATE);
        }
        setActiveTabKey('general');
    }, [initialData, isOpen, parseZonesFromData]);

    if (!isOpen) return null;

    /**
     * Actualiza una propiedad del estado del formulario raíz.
     */
    const handleFieldChange = (fieldName, fieldValue) => {
        setThemeFormState((previousState) => ({
            ...previousState,
            [fieldName]: fieldValue
        }));
    };

    /**
     * Cambia el modo de juego aplicando sugerencias inteligentes si los valores actuales están por defecto.
     */
    const handleGameModeChange = (modeId) => {
        const modeConfig = GAME_MODES.find((m) => m.id === modeId);
        if (!modeConfig) return;

        setThemeFormState((prev) => {
            const isDefaultCover = !prev.coverTitle || GAME_MODES.some((m) => m.defaultCoverTitle === prev.coverTitle);
            const isDefaultWinner = !prev.winnerTextTemplate || GAME_MODES.some((m) => m.defaultWinnerText === prev.winnerTextTemplate);

            return {
                ...prev,
                gameModeType: modeId,
                hasBall: modeConfig.defaultHasBall,
                coverTitle: isDefaultCover ? modeConfig.defaultCoverTitle : prev.coverTitle,
                winnerTextTemplate: isDefaultWinner ? modeConfig.defaultWinnerText : prev.winnerTextTemplate,
                trackBackgroundColor: prev.trackBackgroundType === 'color' ? modeConfig.defaultTrackBgColor : prev.trackBackgroundColor,
                outroElementUrl: modeId === 'soccer' ? (prev.outroElementUrl || modeConfig.defaultOutroElement) : prev.outroElementUrl
            };
        });
    };

    // =========================================================================
    // GESTIÓN DINÁMICA DE N ZONAS
    // =========================================================================

    const handleAddZone = () => {
        setThemeFormState((prev) => {
            const currentZones = prev.zones || [];
            const nextIndex = currentZones.length + 1;
            const newColor = ZONE_PALETTE_COLORS[(nextIndex - 1) % ZONE_PALETTE_COLORS.length];
            const newZone = {
                zoneId: `zona${nextIndex}`,
                name: `Zona ${nextIndex}`,
                color: newColor,
                avatarUrl: '',
                sparkUrl: '',
                winnerAvatarUrl: ''
            };
            return {
                ...prev,
                zones: [...currentZones, newZone]
            };
        });
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Zona agregada. Configura sus avatares y color.' });
    };

    const handleRemoveZone = (indexToRemove) => {
        setThemeFormState((prev) => {
            const currentZones = prev.zones || [];
            if (currentZones.length <= 1) {
                showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debe existir al menos 1 zona configurada.' });
                return prev;
            }
            return {
                ...prev,
                zones: currentZones.filter((_, idx) => idx !== indexToRemove)
            };
        });
    };

    const handleUpdateZoneField = (zoneIndex, fieldKey, fieldValue) => {
        setThemeFormState((prev) => {
            const currentZones = [...(prev.zones || [])];
            if (currentZones[zoneIndex]) {
                currentZones[zoneIndex] = {
                    ...currentZones[zoneIndex],
                    [fieldKey]: fieldValue
                };
            }
            return {
                ...prev,
                zones: currentZones
            };
        });
    };

    const handleSyncStadiumZones = async () => {
        try {
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Consultando zonas activas del estadio...' });
            const response = await GetActiveStadiumZones(roomId);
            if (response && response.success && Array.isArray(response.data) && response.data.length > 0) {
                const syncedZones = response.data.map((stadiumZone, idx) => ({
                    zoneId: String(stadiumZone.id || stadiumZone.internalName || `zona${idx + 1}`),
                    name: stadiumZone.displayName || stadiumZone.name || `Zona ${idx + 1}`,
                    color: ZONE_PALETTE_COLORS[idx % ZONE_PALETTE_COLORS.length],
                    avatarUrl: '',
                    sparkUrl: '',
                    winnerAvatarUrl: ''
                }));

                setThemeFormState((prev) => ({
                    ...prev,
                    zones: syncedZones
                }));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Sincronizadas ${syncedZones.length} zonas desde la sala.` });
            } else {
                showMessage({ type: MESSAGE_TYPE.WARNING, text: 'No se encontraron zonas configuradas en esta sala. Se conservan las actuales.' });
            }
        } catch (e) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al consultar zonas de la sala.' });
        }
    };

    // =========================================================================
    // SUBIDA Y ELIMINACIÓN DE MULTIMEDIA
    // =========================================================================

    const handleUploadMediaFile = async (file, category, fieldName, zoneIndex = -1, zoneSubField = null) => {
        if (!file) return;

        const uploadKey = zoneIndex >= 0 && zoneSubField ? `zone-${zoneIndex}-${zoneSubField}` : fieldName;
        setActiveUploadFieldKey(uploadKey);

        try {
            const uploadResponse = await UploadMedia(file, category, fieldName);
            if (uploadResponse && uploadResponse.success && uploadResponse.url) {
                if (zoneIndex >= 0 && zoneSubField) {
                    handleUpdateZoneField(zoneIndex, zoneSubField, uploadResponse.url);
                } else {
                    handleFieldChange(fieldName, uploadResponse.url);
                }
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Archivo multimedia optimizado y cargado correctamente.' });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: uploadResponse?.message || 'Error al procesar el archivo en el servidor.'
                });
            }
        } catch (uploadException) {
            console.error('[RunToGoalThemeModal] Error en subida:', uploadException);
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de red o comunicación al subir el archivo.' });
        } finally {
            setActiveUploadFieldKey('');
        }
    };

    const handleRemoveMediaFile = (fieldName, zoneIndex = -1, zoneSubField = null) => {
        if (zoneIndex >= 0 && zoneSubField) {
            handleUpdateZoneField(zoneIndex, zoneSubField, '');
        } else {
            handleFieldChange(fieldName, '');
        }
    };

    // =========================================================================
    // VALIDACIÓN Y GUARDADO FINAL
    // =========================================================================

    const handleSaveThemeSubmit = async (formSubmitEvent) => {
        if (formSubmitEvent) formSubmitEvent.preventDefault();

        if (!themeFormState.name || !themeFormState.name.trim()) {
            setActiveTabKey('general');
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'El nombre del tema es obligatorio.' });
            return;
        }

        if (!themeFormState.zones || themeFormState.zones.length === 0) {
            setActiveTabKey('zones');
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Debes configurar al menos 1 zona o carril.' });
            return;
        }

        // Sanitización defensiva de zonas
        const sanitizedZones = themeFormState.zones.map((z, idx) => ({
            zoneId: (z.zoneId || `zona${idx + 1}`).trim().toLowerCase(),
            name: (z.name || `Zona ${idx + 1}`).trim(),
            color: z.color || ZONE_PALETTE_COLORS[idx % ZONE_PALETTE_COLORS.length],
            avatarUrl: z.avatarUrl ? z.avatarUrl.trim() : null,
            sparkUrl: z.sparkUrl ? z.sparkUrl.trim() : null,
            winnerAvatarUrl: z.winnerAvatarUrl ? z.winnerAvatarUrl.trim() : (z.avatarUrl ? z.avatarUrl.trim() : null)
        }));

        setIsSavingInProgress(true);
        try {
            const payload = {
                id: Number(themeFormState.id) || 0,
                name: themeFormState.name.trim(),
                gameModeType: themeFormState.gameModeType || 'soccer',
                roomId: roomId || null,
                operatorId: operatorId || null,
                isSystemPreset: Boolean(themeFormState.isSystemPreset),

                // Portada
                coverTitle: themeFormState.coverTitle ? themeFormState.coverTitle.trim() : null,
                coverSubtitle: themeFormState.coverSubtitle ? themeFormState.coverSubtitle.trim() : null,
                coverBackgroundType: themeFormState.coverBackgroundType || 'color',
                coverBackgroundColor: themeFormState.coverBackgroundColor || '#0f172a',
                coverBackgroundUrl: themeFormState.coverBackgroundUrl ? themeFormState.coverBackgroundUrl.trim() : null,
                coverTextColor: themeFormState.coverTextColor || '#ffffff',
                brandLogoUrl: themeFormState.brandLogoUrl ? themeFormState.brandLogoUrl.trim() : null,
                sponsorLogoUrl: themeFormState.sponsorLogoUrl ? themeFormState.sponsorLogoUrl.trim() : null,

                // Pista
                trackBackgroundType: themeFormState.trackBackgroundType || 'color',
                trackBackgroundColor: themeFormState.trackBackgroundColor || '#064e3b',
                trackBackgroundUrl: themeFormState.trackBackgroundUrl ? themeFormState.trackBackgroundUrl.trim() : null,
                trackTextColor: themeFormState.trackTextColor || '#ffffff',
                finishGapBase: Number(themeFormState.finishGapBase) || 20,
                finishGapStep: Number(themeFormState.finishGapStep) || 4,
                hasBall: Boolean(themeFormState.hasBall),

                // Zonas Dinámicas
                zonesConfigJson: JSON.stringify(sanitizedZones),
                zones: sanitizedZones,

                // Animación Outro
                outroBackgroundUrl: themeFormState.outroBackgroundUrl ? themeFormState.outroBackgroundUrl.trim() : null,
                outroElementUrl: themeFormState.outroElementUrl ? themeFormState.outroElementUrl.trim() : null,
                outroElementWidth: Number(themeFormState.outroElementWidth) || 300,
                outroTranslateY: Number(themeFormState.outroTranslateY) || 0,
                outroScale: Number(themeFormState.outroScale) || 1.0,
                winnerTextTemplate: themeFormState.winnerTextTemplate ? themeFormState.winnerTextTemplate.trim() : '¡GOOOOOL DE {GANADOR}!',

                // Vista Móvil
                fanBackgroundType: themeFormState.fanBackgroundType || 'color',
                fanBackgroundColor: themeFormState.fanBackgroundColor || '#0f172a',
                fanBackgroundUrl: themeFormState.fanBackgroundUrl ? themeFormState.fanBackgroundUrl.trim() : null,
                fanTextColor: themeFormState.fanTextColor || '#ffffff',
                fanButtonIcon: themeFormState.fanButtonIcon ? themeFormState.fanButtonIcon.trim() : null
            };

            const saveResult = await SaveTheme(payload);
            if (saveResult && saveResult.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: payload.id > 0 ? 'Tema actualizado exitosamente.' : 'Tema creado exitosamente.'
                });
                handleThemeSaved(saveResult.data || payload);
                handleClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: saveResult?.message || 'Error al guardar el tema en la base de datos.'
                });
            }
        } catch (saveError) {
            console.error('[RunToGoalThemeModal] Error al guardar tema:', saveError);
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al intentar guardar el tema.' });
        } finally {
            setIsSavingInProgress(false);
        }
    };

    return (
        <>
            <div className="cModal__Background" onClick={handleClose}></div>
            <div className="cModal__Container" style={{ zIndex: 99990 }}>
                <div
                    className="cModal__Container__Content RunToGoalThemeModal position-relative p-0 overflow-hidden bg-white shadow-lg rounded-4"
                    style={{ maxWidth: '920px', width: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
                >
                    {/* ENCABEZADO DEL MODAL */}
                    <div className="bg-dark text-white p-3 px-4 d-flex justify-content-between align-items-center border-bottom border-secondary">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-dribbble text-warning fs-4"></i>
                            <div>
                                <h5 className="m-0 fw-bold">
                                    {themeFormState.id > 0 ? `Editar Tema: ${themeFormState.name}` : 'Crear Nuevo Tema: Tiro a Gol'}
                                </h5>
                                <span className="small text-muted">
                                    Configura escenas, pista, avatares por zona, animaciones de meta y vista móvil
                                </span>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center"
                            onClick={handleClose}
                            style={{ width: '32px', height: '32px' }}
                            title="Cerrar ventana"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* BARRA DE NAVEGACIÓN DE PESTAÑAS */}
                    <div className="bg-light border-bottom px-3 pt-2">
                        <ul className="nav nav-tabs border-0 flex-nowrap overflow-x-auto">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold text-nowrap ${activeTabKey === 'general' ? 'active text-primary border-bottom border-primary border-3' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('general')}
                                >
                                    <i className="bi bi-card-heading me-1"></i> 1. General y Portada
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold text-nowrap ${activeTabKey === 'track' ? 'active text-primary border-bottom border-primary border-3' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('track')}
                                >
                                    <i className="bi bi-signpost-split me-1"></i> 2. Pista y Modo
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold text-nowrap ${activeTabKey === 'zones' ? 'active text-primary border-bottom border-primary border-3' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('zones')}
                                >
                                    <i className="bi bi-people-fill me-1"></i> 3. N Zonas / Carriles ({themeFormState.zones?.length || 0})
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold text-nowrap ${activeTabKey === 'outro' ? 'active text-primary border-bottom border-primary border-3' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('outro')}
                                >
                                    <i className="bi bi-trophy me-1"></i> 4. Outro / Meta
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold text-nowrap ${activeTabKey === 'mobile' ? 'active text-primary border-bottom border-primary border-3' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('mobile')}
                                >
                                    <i className="bi bi-phone me-1"></i> 5. Vista Móvil
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* CUERPO DEL FORMULARIO CON SCROLL */}
                    <div className="p-4 overflow-y-auto flex-grow-1" style={{ maxHeight: 'calc(90vh - 150px)' }}>
                        
                        {/* ======================================================== */}
                        {/* PESTAÑA 1: GENERAL Y PORTADA                             */}
                        {/* ======================================================== */}
                        {activeTabKey === 'general' && (
                            <div className="row g-3">
                                <div className="col-12">
                                    <div className="alert alert-primary py-2 px-3 small d-flex align-items-center gap-2 mb-3">
                                        <i className="bi bi-info-circle-fill fs-5"></i>
                                        <span>
                                            Configura los datos principales del tema, el modo de juego base y los elementos de la portada proyectada en pantalla gigante.
                                        </span>
                                    </div>
                                </div>

                                <div className="col-md-7">
                                    <label className="form-label fw-bold small text-dark">
                                        Nombre del Tema <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control fw-bold"
                                        placeholder="Ej: Tiro a Gol - Xolos vs Rayados"
                                        value={themeFormState.name}
                                        onChange={(e) => handleFieldChange('name', e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="col-md-5">
                                    <label className="form-label fw-bold small text-dark">
                                        Modo de Juego / Género
                                    </label>
                                    <select
                                        className="form-select fw-bold border-primary"
                                        value={themeFormState.gameModeType}
                                        onChange={(e) => handleGameModeChange(e.target.value)}
                                    >
                                        {GAME_MODES.map((m) => (
                                            <option key={m.id} value={m.id}>
                                                {m.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Selector visual de modos */}
                                <div className="col-12">
                                    <div className="row g-2">
                                        {GAME_MODES.map((m) => (
                                            <div key={m.id} className="col-sm-6 col-lg-3">
                                                <div
                                                    className={`card h-100 p-2 border-2 cursor-pointer transition-all ${
                                                        themeFormState.gameModeType === m.id
                                                            ? 'border-primary bg-primary-subtle shadow-sm'
                                                            : 'border-light bg-light opacity-75'
                                                    }`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleGameModeChange(m.id)}
                                                >
                                                    <div className="d-flex align-items-center gap-2 mb-1">
                                                        <i className={`bi ${m.icon} text-primary fs-5`}></i>
                                                        <span className="fw-bold small">{m.name.split(' ')[0]}</span>
                                                    </div>
                                                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        {m.description}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="col-12 border-top pt-3 mt-3">
                                    <h6 className="fw-bold text-dark mb-3">
                                        <i className="bi bi-display text-primary me-1"></i> Textos y Logotipos de Portada
                                    </h6>
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-bold small text-dark">Título de Portada</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Ej: ¡TIRO A GOL!"
                                        value={themeFormState.coverTitle}
                                        onChange={(e) => handleFieldChange('coverTitle', e.target.value)}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-bold small text-dark">Subtítulo de Portada</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Ej: ¡Presiona para que tu equipo anote!"
                                        value={themeFormState.coverSubtitle}
                                        onChange={(e) => handleFieldChange('coverSubtitle', e.target.value)}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <DropzoneField
                                        label="Logo de Marca (Principal)"
                                        fieldName="brandLogoUrl"
                                        category="cover"
                                        value={themeFormState.brandLogoUrl}
                                        onUpload={handleUploadMediaFile}
                                        onRemove={handleRemoveMediaFile}
                                        isUploading={activeUploadFieldKey === 'brandLogoUrl'}
                                        aspectHint="PNG transparente"
                                        helpText="Logo institucional o del evento en la portada"
                                    />
                                </div>

                                <div className="col-md-6">
                                    <DropzoneField
                                        label="Logo de Patrocinador"
                                        fieldName="sponsorLogoUrl"
                                        category="cover"
                                        value={themeFormState.sponsorLogoUrl}
                                        onUpload={handleUploadMediaFile}
                                        onRemove={handleRemoveMediaFile}
                                        isUploading={activeUploadFieldKey === 'sponsorLogoUrl'}
                                        aspectHint="PNG transparente"
                                        helpText="Logo del sponsor oficial de la dinámica"
                                    />
                                </div>

                                <div className="col-12 border-top pt-3 mt-3">
                                    <h6 className="fw-bold text-dark mb-3">
                                        <i className="bi bi-palette text-primary me-1"></i> Fondo y Colores de Portada
                                    </h6>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-dark">Tipo de Fondo</label>
                                    <select
                                        className="form-select"
                                        value={themeFormState.coverBackgroundType}
                                        onChange={(e) => handleFieldChange('coverBackgroundType', e.target.value)}
                                    >
                                        <option value="color">Color Sólido</option>
                                        <option value="image">Imagen Personalizada</option>
                                    </select>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-dark">Color de Fondo</label>
                                    <div className="input-group">
                                        <input
                                            type="color"
                                            className="form-control form-control-color p-1"
                                            value={themeFormState.coverBackgroundColor}
                                            onChange={(e) => handleFieldChange('coverBackgroundColor', e.target.value)}
                                        />
                                        <input
                                            type="text"
                                            className="form-control font-monospace"
                                            value={themeFormState.coverBackgroundColor}
                                            onChange={(e) => handleFieldChange('coverBackgroundColor', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-dark">Color de Texto</label>
                                    <div className="input-group">
                                        <input
                                            type="color"
                                            className="form-control form-control-color p-1"
                                            value={themeFormState.coverTextColor}
                                            onChange={(e) => handleFieldChange('coverTextColor', e.target.value)}
                                        />
                                        <input
                                            type="text"
                                            className="form-control font-monospace"
                                            value={themeFormState.coverTextColor}
                                            onChange={(e) => handleFieldChange('coverTextColor', e.target.value)}
                                        />
                                    </div>
                                </div>

                                {themeFormState.coverBackgroundType === 'image' && (
                                    <div className="col-12">
                                        <DropzoneField
                                            label="Imagen de Fondo de Portada"
                                            fieldName="coverBackgroundUrl"
                                            category="cover"
                                            value={themeFormState.coverBackgroundUrl}
                                            onUpload={handleUploadMediaFile}
                                            onRemove={handleRemoveMediaFile}
                                            isUploading={activeUploadFieldKey === 'coverBackgroundUrl'}
                                            aspectHint="1920x1080 Full HD (16:9)"
                                            helpText="Fondo principal para la portada gigante"
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ======================================================== */}
                        {/* PESTAÑA 2: PISTA Y MODO                                  */}
                        {/* ======================================================== */}
                        {activeTabKey === 'track' && (
                            <div className="row g-3">
                                <div className="col-12">
                                    <div className="alert alert-primary py-2 px-3 small d-flex align-items-center gap-2 mb-3">
                                        <i className="bi bi-info-circle-fill fs-5"></i>
                                        <span>
                                            Personaliza el escenario donde compiten los corredores (cancha de fútbol, asfalto, pista de tartán) y la física de avance hacia la meta.
                                        </span>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-dark">Tipo de Fondo de Pista</label>
                                    <select
                                        className="form-select"
                                        value={themeFormState.trackBackgroundType}
                                        onChange={(e) => handleFieldChange('trackBackgroundType', e.target.value)}
                                    >
                                        <option value="color">Color Sólido</option>
                                        <option value="image">Imagen Personalizada</option>
                                    </select>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-dark">Color de Fondo</label>
                                    <div className="input-group">
                                        <input
                                            type="color"
                                            className="form-control form-control-color p-1"
                                            value={themeFormState.trackBackgroundColor}
                                            onChange={(e) => handleFieldChange('trackBackgroundColor', e.target.value)}
                                        />
                                        <input
                                            type="text"
                                            className="form-control font-monospace"
                                            value={themeFormState.trackBackgroundColor}
                                            onChange={(e) => handleFieldChange('trackBackgroundColor', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-dark">Color de Texto de Pista</label>
                                    <div className="input-group">
                                        <input
                                            type="color"
                                            className="form-control form-control-color p-1"
                                            value={themeFormState.trackTextColor}
                                            onChange={(e) => handleFieldChange('trackTextColor', e.target.value)}
                                        />
                                        <input
                                            type="text"
                                            className="form-control font-monospace"
                                            value={themeFormState.trackTextColor}
                                            onChange={(e) => handleFieldChange('trackTextColor', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="col-12">
                                    <DropzoneField
                                        label="Imagen de Fondo de la Pista / Cancha"
                                        fieldName="trackBackgroundUrl"
                                        category="track"
                                        value={themeFormState.trackBackgroundUrl}
                                        onUpload={handleUploadMediaFile}
                                        onRemove={handleRemoveMediaFile}
                                        isUploading={activeUploadFieldKey === 'trackBackgroundUrl'}
                                        aspectHint="1920x1080 (16:9) Pista horizontal"
                                        helpText="Escenario donde se desplazan los corredores de izquierda a derecha"
                                    />
                                </div>

                                <div className="col-12 border-top pt-3 mt-3">
                                    <h6 className="fw-bold text-dark mb-3">
                                        <i className="bi bi-sliders text-primary me-1"></i> Mecánicas de Carrera y Meta
                                    </h6>
                                </div>

                                <div className="col-md-4">
                                    <div className="form-check form-switch p-3 bg-light rounded border h-100 d-flex flex-column justify-content-center">
                                        <input
                                            className="form-check-input fs-5 ms-0 me-2"
                                            type="checkbox"
                                            id="hasBallCheck"
                                            checked={themeFormState.hasBall}
                                            onChange={(e) => handleFieldChange('hasBall', e.target.checked)}
                                        />
                                        <label className="form-check-label fw-bold small text-dark" htmlFor="hasBallCheck">
                                            ¿Tiene Balón Rodante?
                                        </label>
                                        <span className="text-muted small d-block mt-1">
                                            Activa la pelota rodante delante del avatar de cada zona.
                                        </span>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-dark">
                                        Gap de Meta Base (FinishGapBase)
                                    </label>
                                    <div className="input-group">
                                        <input
                                            type="number"
                                            className="form-control fw-bold"
                                            value={themeFormState.finishGapBase}
                                            onChange={(e) => handleFieldChange('finishGapBase', Number(e.target.value) || 0)}
                                            min={0}
                                            max={100}
                                        />
                                        <span className="input-group-text">px / %</span>
                                    </div>
                                    <small className="text-muted">Desfase inicial de la línea de meta (por defecto: 20).</small>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-dark">
                                        Paso de Desfase por Carril (FinishGapStep)
                                    </label>
                                    <div className="input-group">
                                        <input
                                            type="number"
                                            className="form-control fw-bold"
                                            value={themeFormState.finishGapStep}
                                            onChange={(e) => handleFieldChange('finishGapStep', Number(e.target.value) || 0)}
                                            min={0}
                                            max={50}
                                        />
                                        <span className="input-group-text">px</span>
                                    </div>
                                    <small className="text-muted">Desfase escalonado por carril (por defecto: 4).</small>
                                </div>
                            </div>
                        )}

                        {/* ======================================================== */}
                        {/* PESTAÑA 3: CONFIGURACIÓN DE N ZONAS / CARRILES           */}
                        {/* ======================================================== */}
                        {activeTabKey === 'zones' && (
                            <div className="row g-3">
                                <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                                    <div>
                                        <h6 className="fw-bold text-dark m-0">
                                            <i className="bi bi-people-fill text-primary me-1"></i> Zonas / Carriles Dinámicos ({themeFormState.zones?.length || 0})
                                        </h6>
                                        <span className="small text-muted">
                                            Configura los equipos o zonas del estadio que participan en la carrera simultáneamente.
                                        </span>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary fw-bold shadow-sm"
                                            onClick={handleSyncStadiumZones}
                                            title="Importar nombres de zonas configuradas en esta sala"
                                        >
                                            <i className="bi bi-arrow-repeat me-1"></i> Sincronizar de Sala
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-success fw-bold shadow-sm"
                                            onClick={handleAddZone}
                                        >
                                            <i className="bi bi-plus-lg me-1"></i> Agregar Zona
                                        </button>
                                    </div>
                                </div>

                                {themeFormState.zones && themeFormState.zones.map((zone, zIdx) => (
                                    <div key={zIdx} className="col-12">
                                        <div className="card shadow-sm border rounded-3 overflow-hidden">
                                            <div
                                                className="card-header p-2 px-3 d-flex justify-content-between align-items-center"
                                                style={{
                                                    borderLeft: `6px solid ${zone.color || '#3b82f6'}`,
                                                    backgroundColor: '#f8fafc'
                                                }}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="badge bg-dark">Carril {zIdx + 1}</span>
                                                    <strong className="text-dark">{zone.name || `Zona ${zIdx + 1}`}</strong>
                                                    <span className="badge bg-light text-muted border font-monospace small">
                                                        ID: {zone.zoneId || `zona${zIdx + 1}`}
                                                    </span>
                                                </div>

                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger border-0 p-1 px-2"
                                                    title="Eliminar esta zona"
                                                    onClick={() => handleRemoveZone(zIdx)}
                                                    disabled={themeFormState.zones.length <= 1}
                                                >
                                                    <i className="bi bi-trash3-fill"></i>
                                                </button>
                                            </div>

                                            <div className="card-body p-3 bg-white">
                                                <div className="row g-3">
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-bold small text-dark mb-1">
                                                            ID de Zona (Taps & WS)
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm font-monospace"
                                                            placeholder="ej: zona1"
                                                            value={zone.zoneId}
                                                            onChange={(e) => handleUpdateZoneField(zIdx, 'zoneId', e.target.value)}
                                                        />
                                                    </div>

                                                    <div className="col-md-5">
                                                        <label className="form-label fw-bold small text-dark mb-1">
                                                            Nombre Visual del Equipo / Zona
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm fw-bold"
                                                            placeholder="ej: Xolo Mayor Rojo"
                                                            value={zone.name}
                                                            onChange={(e) => handleUpdateZoneField(zIdx, 'name', e.target.value)}
                                                        />
                                                    </div>

                                                    <div className="col-md-3">
                                                        <label className="form-label fw-bold small text-dark mb-1">
                                                            Color Temático
                                                        </label>
                                                        <div className="input-group input-group-sm">
                                                            <input
                                                                type="color"
                                                                className="form-control form-control-color p-0"
                                                                value={zone.color}
                                                                onChange={(e) => handleUpdateZoneField(zIdx, 'color', e.target.value)}
                                                            />
                                                            <input
                                                                type="text"
                                                                className="form-control font-monospace"
                                                                value={zone.color}
                                                                onChange={(e) => handleUpdateZoneField(zIdx, 'color', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Dropzones de la Zona */}
                                                    <div className="col-md-4">
                                                        <DropzoneField
                                                            label="Avatar en Carrera (Sprite)"
                                                            fieldName="avatarUrl"
                                                            category="zones"
                                                            zoneIndex={zIdx}
                                                            zoneSubField="avatarUrl"
                                                            value={zone.avatarUrl}
                                                            onUpload={handleUploadMediaFile}
                                                            onRemove={handleRemoveMediaFile}
                                                            isUploading={activeUploadFieldKey === `zone-${zIdx}-avatarUrl`}
                                                            aspectHint="PNG transparente"
                                                            helpText="Sprite animado del corredor"
                                                            previewHeight="65px"
                                                        />
                                                    </div>

                                                    <div className="col-md-4">
                                                        <DropzoneField
                                                            label="Destello / Partículas"
                                                            fieldName="sparkUrl"
                                                            category="zones"
                                                            zoneIndex={zIdx}
                                                            zoneSubField="sparkUrl"
                                                            value={zone.sparkUrl}
                                                            onUpload={handleUploadMediaFile}
                                                            onRemove={handleRemoveMediaFile}
                                                            isUploading={activeUploadFieldKey === `zone-${zIdx}-sparkUrl`}
                                                            aspectHint="PNG transparente"
                                                            helpText="Rastro de fuego / velocidad"
                                                            previewHeight="65px"
                                                        />
                                                    </div>

                                                    <div className="col-md-4">
                                                        <DropzoneField
                                                            label="Avatar Ganador (Celebración)"
                                                            fieldName="winnerAvatarUrl"
                                                            category="zones"
                                                            zoneIndex={zIdx}
                                                            zoneSubField="winnerAvatarUrl"
                                                            value={zone.winnerAvatarUrl}
                                                            onUpload={handleUploadMediaFile}
                                                            onRemove={handleRemoveMediaFile}
                                                            isUploading={activeUploadFieldKey === `zone-${zIdx}-winnerAvatarUrl`}
                                                            aspectHint="PNG transparente"
                                                            helpText="Mascota festejando victoria"
                                                            previewHeight="65px"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* ======================================================== */}
                        {/* PESTAÑA 4: ANIMACIÓN OUTRO / META                        */}
                        {/* ======================================================== */}
                        {activeTabKey === 'outro' && (
                            <div className="row g-3">
                                <div className="col-12">
                                    <div className="alert alert-primary py-2 px-3 small d-flex align-items-center gap-2 mb-3">
                                        <i className="bi bi-info-circle-fill fs-5"></i>
                                        <span>
                                            Personaliza la cinemática final de victoria (Outro): el fondo de portería o arco de meta, el elemento central animado (balón, coche, trofeo) y la plantilla del mensaje triunfal.
                                        </span>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <DropzoneField
                                        label="Fondo de Meta (Portería / Arco)"
                                        fieldName="outroBackgroundUrl"
                                        category="outro"
                                        value={themeFormState.outroBackgroundUrl}
                                        onUpload={handleUploadMediaFile}
                                        onRemove={handleRemoveMediaFile}
                                        isUploading={activeUploadFieldKey === 'outroBackgroundUrl'}
                                        aspectHint="1920x1080 Full HD"
                                        helpText="Fondo estático o portería en primer plano"
                                    />
                                </div>

                                <div className="col-md-6">
                                    <DropzoneField
                                        label="Elemento Central Animado"
                                        fieldName="outroElementUrl"
                                        category="outro"
                                        value={themeFormState.outroElementUrl}
                                        onUpload={handleUploadMediaFile}
                                        onRemove={handleRemoveMediaFile}
                                        isUploading={activeUploadFieldKey === 'outroElementUrl'}
                                        aspectHint="PNG transparente"
                                        helpText="Balón gigante, trofeo o auto que escala hacia la cámara"
                                    />
                                </div>

                                <div className="col-12 border-top pt-3 mt-3">
                                    <h6 className="fw-bold text-dark mb-3">
                                        <i className="bi bi-arrows-fullscreen text-primary me-1"></i> Dimensiones y Transformación de la Animación
                                    </h6>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-dark">Ancho del Elemento (px)</label>
                                    <div className="input-group">
                                        <input
                                            type="number"
                                            className="form-control fw-bold"
                                            value={themeFormState.outroElementWidth}
                                            onChange={(e) => handleFieldChange('outroElementWidth', Number(e.target.value) || 0)}
                                            min={50}
                                            max={1200}
                                        />
                                        <span className="input-group-text">px</span>
                                    </div>
                                    <small className="text-muted">Ancho base del elemento central (default: 300px).</small>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-dark">Traslación Y (Translate Y)</label>
                                    <div className="input-group">
                                        <input
                                            type="number"
                                            className="form-control fw-bold"
                                            value={themeFormState.outroTranslateY}
                                            onChange={(e) => handleFieldChange('outroTranslateY', Number(e.target.value) || 0)}
                                        />
                                        <span className="input-group-text">px</span>
                                    </div>
                                    <small className="text-muted">Ajuste vertical de elevación o descenso (default: 0px).</small>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-dark">Escala Final (Scale)</label>
                                    <div className="input-group">
                                        <input
                                            type="number"
                                            step="0.1"
                                            className="form-control fw-bold"
                                            value={themeFormState.outroScale}
                                            onChange={(e) => handleFieldChange('outroScale', Number(e.target.value) || 1.0)}
                                            min={0.1}
                                            max={5.0}
                                        />
                                        <span className="input-group-text">x</span>
                                    </div>
                                    <small className="text-muted">Multiplicador de escala en clímax (default: 1.0).</small>
                                </div>

                                <div className="col-12 border-top pt-3 mt-3">
                                    <label className="form-label fw-bold small text-dark">
                                        Plantilla de Texto del Ganador
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control fw-bold text-success border-success"
                                        placeholder="Ej: ¡GOOOOOL DE {GANADOR}!"
                                        value={themeFormState.winnerTextTemplate}
                                        onChange={(e) => handleFieldChange('winnerTextTemplate', e.target.value)}
                                    />
                                    <small className="text-muted d-block mt-1">
                                        Variables disponibles: <code>{'{GANADOR}'}</code>, <code>{'{ZONE_NAME}'}</code>, <code>{'{ZONE_ID}'}</code>.
                                    </small>
                                </div>
                            </div>
                        )}

                        {/* ======================================================== */}
                        {/* PESTAÑA 5: VISTA MÓVIL                                   */}
                        {/* ======================================================== */}
                        {activeTabKey === 'mobile' && (
                            <div className="row g-3">
                                <div className="col-12">
                                    <div className="alert alert-primary py-2 px-3 small d-flex align-items-center gap-2 mb-3">
                                        <i className="bi bi-info-circle-fill fs-5"></i>
                                        <span>
                                            Personaliza la apariencia que los aficionados experimentan en sus dispositivos móviles al unirse a la sala para presionar la pantalla.
                                        </span>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-dark">Tipo de Fondo Móvil</label>
                                    <select
                                        className="form-select"
                                        value={themeFormState.fanBackgroundType}
                                        onChange={(e) => handleFieldChange('fanBackgroundType', e.target.value)}
                                    >
                                        <option value="color">Color Sólido</option>
                                        <option value="image">Imagen Personalizada</option>
                                    </select>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-dark">Color de Fondo</label>
                                    <div className="input-group">
                                        <input
                                            type="color"
                                            className="form-control form-control-color p-1"
                                            value={themeFormState.fanBackgroundColor}
                                            onChange={(e) => handleFieldChange('fanBackgroundColor', e.target.value)}
                                        />
                                        <input
                                            type="text"
                                            className="form-control font-monospace"
                                            value={themeFormState.fanBackgroundColor}
                                            onChange={(e) => handleFieldChange('fanBackgroundColor', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold small text-dark">Color de Texto</label>
                                    <div className="input-group">
                                        <input
                                            type="color"
                                            className="form-control form-control-color p-1"
                                            value={themeFormState.fanTextColor}
                                            onChange={(e) => handleFieldChange('fanTextColor', e.target.value)}
                                        />
                                        <input
                                            type="text"
                                            className="form-control font-monospace"
                                            value={themeFormState.fanTextColor}
                                            onChange={(e) => handleFieldChange('fanTextColor', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <DropzoneField
                                        label="Imagen de Fondo Móvil"
                                        fieldName="fanBackgroundUrl"
                                        category="fan"
                                        value={themeFormState.fanBackgroundUrl}
                                        onUpload={handleUploadMediaFile}
                                        onRemove={handleRemoveMediaFile}
                                        isUploading={activeUploadFieldKey === 'fanBackgroundUrl'}
                                        aspectHint="1080x1920 Vertical (9:16)"
                                        helpText="Fondo vertical para el navegador del aficionado"
                                    />
                                </div>

                                <div className="col-md-6">
                                    <DropzoneField
                                        label="Ícono Personalizado de Tap"
                                        fieldName="fanButtonIcon"
                                        category="fan"
                                        value={themeFormState.fanButtonIcon}
                                        onUpload={handleUploadMediaFile}
                                        onRemove={handleRemoveMediaFile}
                                        isUploading={activeUploadFieldKey === 'fanButtonIcon'}
                                        aspectHint="PNG transparente 256x256"
                                        helpText="Ícono en el centro del pulsador táctil (balón, zapato, turbo)"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* PIE DE PÁGINA CON BOTONES DE ACCIÓN */}
                    <div className="bg-light p-3 px-4 d-flex justify-content-between align-items-center border-top">
                        <span className="small text-muted font-monospace">
                            {themeFormState.id > 0 ? `ID: ${themeFormState.id}` : 'Nuevo Tema (Borrador)'}
                        </span>

                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary fw-bold px-4"
                                onClick={handleClose}
                                disabled={isSavingInProgress}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary fw-bold px-4 shadow-sm d-flex align-items-center gap-2"
                                onClick={handleSaveThemeSubmit}
                                disabled={isSavingInProgress}
                            >
                                {isSavingInProgress ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm text-white" role="status"></div>
                                        <span>Guardando...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-cloud-check-fill"></i>
                                        <span>Guardar Tema</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

RunToGoalThemeModal.propTypes = {
    isOpen: PropTypes.bool,
    show: PropTypes.bool,
    onCloseModal: PropTypes.func,
    onClose: PropTypes.func,
    initialThemeData: PropTypes.object,
    themeToEdit: PropTypes.object,
    onThemeSavedSuccessfully: PropTypes.func,
    onThemeSaved: PropTypes.func,
    onSave: PropTypes.func,
    roomId: PropTypes.string,
    operatorId: PropTypes.string
};

export default RunToGoalThemeModal;
